import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, ExternalLink } from 'lucide-react';
import { Receipts } from '@/integrations/api';
import { useIsMobile } from '@/hooks/use-mobile';

interface ReceiptModalProps {
  open: boolean;
  onClose: () => void;
  receiptUrl: string | null;
  expenseId: string;
}

export function ReceiptModal({ open, onClose, receiptUrl, expenseId }: ReceiptModalProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const isInTeams = window.navigator.userAgent.includes('Teams');

  useEffect(() => {
    if (!open || !receiptUrl) return;

    let active = true;
    let createdUrl: string | null = null;

    const loadReceipt = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = await Receipts.objectUrl(receiptUrl);
        createdUrl = url;
        if (active) {
          setObjectUrl(url);
        } else {
          // Component unmounted / re-ran before we could use the URL.
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error('Error loading receipt:', error);
        if (active) setError('Failed to load receipt');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadReceipt();

    // Revoke the blob URL on close/unmount to avoid leaks.
    return () => {
      active = false;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
      setObjectUrl(null);
    };
  }, [open, receiptUrl]);

  const handleDownload = async () => {
    if (!objectUrl) return;

    // For mobile devices or Teams app, open in new tab instead of downloading.
    if (isMobile || isInTeams) {
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Desktop download functionality — the blob URL can be downloaded directly.
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `receipt-${expenseId}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4 pr-14">
          <div className="flex items-center justify-between">
            <DialogTitle>Receipt</DialogTitle>
            {objectUrl && (
              <Button variant="outline" size="sm" onClick={handleDownload}>
                {isMobile || isInTeams ? (
                  <>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Receipt
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <div className="px-6 pb-6">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">Loading receipt...</div>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-destructive">{error}</div>
            </div>
          )}
          
          {objectUrl && !loading && !error && (
            <div className="flex justify-center">
              <img
                src={objectUrl}
                alt="Receipt" 
                className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                onError={() => setError('Failed to display receipt image')}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}